// ============================================
// narb-auth/src/accessibility/AccessibilitySheet.jsx
// ============================================
// The modal panel that opens when the user taps the accessibility
// toggle. Contains every control:
//   - Master enable/disable
//   - Text-to-speech (TTS) on/off
//   - Font size (small / medium / large / extra-large)
//   - Dyslexic-friendly font
//   - High contrast
//   - Reduce motion
//   - Larger tap targets
//   - Disable swipe gestures
//   - Free-text "describe your needs" — for Maia to read and adapt
//
// Focus management:
//   - On open, focus moves to the close button (predictable landing)
//   - Tab cycles within the sheet (focus trap)
//   - On close, focus returns to the toggle button
// ============================================

import React, { useEffect, useRef, useState } from 'react';
import { useAccessibilityStore } from './accessibilityStore.js';

export default function AccessibilitySheet({ token, apiBase, accentColor = '#666', onClose }) {
  const enabled = useAccessibilityStore((s) => s.enabled);
  const prefs = useAccessibilityStore((s) => s.prefs);
  const setEnabled = useAccessibilityStore((s) => s.setEnabled);
  const updatePref = useAccessibilityStore((s) => s.updatePref);

  const closeButtonRef = useRef(null);
  const sheetRef = useRef(null);
  const customNeedsRef = useRef(null);

  // Local state for the custom-needs textarea — debounced before persisting
  const [localCustomNeeds, setLocalCustomNeeds] = useState(prefs.customNeeds || '');

  // Move focus to the close button when the sheet opens
  useEffect(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, []);

  // Debounce custom-needs persistence to avoid hammering the API on every keystroke
  useEffect(() => {
    if (localCustomNeeds === (prefs.customNeeds || '')) return;
    const timer = setTimeout(() => {
      updatePref('customNeeds', localCustomNeeds, { token, apiBase });
    }, 600);
    return () => clearTimeout(timer);
  }, [localCustomNeeds, prefs.customNeeds, updatePref, token, apiBase]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="narbodica-accessibility-sheet-title"
      id="narbodica-accessibility-sheet"
      onClick={(e) => {
        // Click on backdrop closes; click inside sheet doesn't
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        WebkitBackdropFilter: 'blur(2px)',
        backdropFilter: 'blur(2px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        ref={sheetRef}
        style={{
          background: '#fff',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '85vh',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          padding: '20px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
          overflowY: 'auto',
          boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.2)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2
            id="narbodica-accessibility-sheet-title"
            style={{ fontSize: '20px', fontWeight: 600, margin: 0, color: '#111' }}
          >
            Accessibility
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close accessibility settings"
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '24px',
              lineHeight: 1,
              cursor: 'pointer',
              padding: '8px',
              minWidth: '44px',
              minHeight: '44px',
              color: '#666',
            }}
          >
            ✕
          </button>
        </div>

        {/* Master toggle */}
        <SettingRow>
          <ToggleControl
            label="Accessibility mode"
            description="Turn on to enable TTS, larger text, and adapted controls"
            checked={enabled}
            onChange={(value) => setEnabled(value, { token, apiBase })}
            accentColor={accentColor}
          />
        </SettingRow>

        {/* Text-to-speech */}
        <SettingRow>
          <ToggleControl
            label="Read aloud (text-to-speech)"
            description="Maia and the app will speak text aloud"
            checked={prefs.ttsEnabled}
            onChange={(value) => updatePref('ttsEnabled', value, { token, apiBase })}
            accentColor={accentColor}
          />
        </SettingRow>

        {/* Font size */}
        <SettingRow>
          <SegmentedControl
            label="Text size"
            value={prefs.fontSize}
            options={[
              { value: 'small', label: 'Small' },
              { value: 'medium', label: 'Medium' },
              { value: 'large', label: 'Large' },
              { value: 'extra-large', label: 'Extra large' },
            ]}
            onChange={(value) => updatePref('fontSize', value, { token, apiBase })}
            accentColor={accentColor}
          />
        </SettingRow>

        {/* Dyslexic font */}
        <SettingRow>
          <ToggleControl
            label="Dyslexic-friendly font"
            description="Use a font designed to be easier to read for people with dyslexia"
            checked={prefs.dyslexicFont}
            onChange={(value) => updatePref('dyslexicFont', value, { token, apiBase })}
            accentColor={accentColor}
          />
        </SettingRow>

        {/* High contrast */}
        <SettingRow>
          <ToggleControl
            label="High contrast"
            description="Stronger contrast between text and background"
            checked={prefs.highContrast}
            onChange={(value) => updatePref('highContrast', value, { token, apiBase })}
            accentColor={accentColor}
          />
        </SettingRow>

        {/* Reduce motion */}
        <SettingRow>
          <ToggleControl
            label="Reduce motion"
            description="Minimise animations and transitions"
            checked={prefs.reduceMotion}
            onChange={(value) => updatePref('reduceMotion', value, { token, apiBase })}
            accentColor={accentColor}
          />
        </SettingRow>

        {/* Larger tap targets */}
        <SettingRow>
          <ToggleControl
            label="Larger tap targets"
            description="Buttons and links become easier to tap"
            checked={prefs.largerTapTargets}
            onChange={(value) => updatePref('largerTapTargets', value, { token, apiBase })}
            accentColor={accentColor}
          />
        </SettingRow>

        {/* Disable swipe gestures */}
        <SettingRow>
          <ToggleControl
            label="Show buttons instead of swipe"
            description="Replace swipe-only actions with tappable buttons"
            checked={prefs.disableSwipeGestures}
            onChange={(value) => updatePref('disableSwipeGestures', value, { token, apiBase })}
            accentColor={accentColor}
          />
        </SettingRow>

        {/* Custom needs */}
        <SettingRow>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', fontWeight: 500, color: '#111' }}>
            Anything Maia should know about how to support you?
          </label>
          <p style={{ fontSize: '13px', color: '#666', marginTop: 0, marginBottom: '8px' }}>
            Free text — describe your needs in your own words. Maia will read this and adapt.
          </p>
          <textarea
            ref={customNeedsRef}
            value={localCustomNeeds}
            onChange={(e) => setLocalCustomNeeds(e.target.value)}
            placeholder="e.g. I have low vision and use a screen reader. I prefer fewer questions per turn."
            rows={3}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              fontSize: '14px',
              fontFamily: 'inherit',
              border: '1px solid #ddd',
              borderRadius: '8px',
              resize: 'vertical',
              minHeight: '60px',
            }}
          />
        </SettingRow>

        {/* Done button */}
        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: accentColor,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 28px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              minWidth: '120px',
              minHeight: '44px',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Internal sub-components
// ============================================

function SettingRow({ children }) {
  return (
    <div style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
      {children}
    </div>
  );
}

function ToggleControl({ label, description, checked, onChange, accentColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '15px', fontWeight: 500, color: '#111', marginBottom: '2px' }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: '13px', color: '#666' }}>
            {description}
          </div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        style={{
          width: '52px',
          height: '32px',
          minHeight: '32px',
          borderRadius: '999px',
          border: 'none',
          background: checked ? accentColor : '#ddd',
          padding: '2px',
          cursor: 'pointer',
          flexShrink: 0,
          position: 'relative',
          transition: 'background 200ms ease',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'block',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
            transform: checked ? 'translateX(20px)' : 'translateX(0)',
            transition: 'transform 200ms ease',
          }}
        />
      </button>
    </div>
  );
}

function SegmentedControl({ label, value, options, onChange, accentColor }) {
  return (
    <div>
      <div style={{ fontSize: '15px', fontWeight: 500, color: '#111', marginBottom: '8px' }}>
        {label}
      </div>
      <div
        role="radiogroup"
        aria-label={label}
        style={{
          display: 'flex',
          gap: '6px',
          flexWrap: 'wrap',
        }}
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              style={{
                flex: 1,
                minWidth: '70px',
                minHeight: '40px',
                padding: '10px 12px',
                border: `2px solid ${selected ? accentColor : '#ddd'}`,
                borderRadius: '8px',
                background: selected ? accentColor : '#fff',
                color: selected ? '#fff' : '#333',
                fontSize: '14px',
                fontWeight: selected ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
